/* eslint-disable no-restricted-syntax */
import {
  ControlUIType,
  MultiOptions,
  ProductUnAvailability,
  ProductVariation,
  SingleOptions,
  Product,
} from 'editor/src/store/variants/types';

import ButtonElement from './ProductControls/elements/ButtonElement';
import CheckboxElement from './ProductControls/elements/CheckboxElement';
import ColorElement from './ProductControls/elements/ColorElement';
import MaterialElement from './ProductControls/elements/MaterialElement';
import RadioElement from './ProductControls/elements/RadioElement';
import MultiControl from './ProductControls/multi/MultiControl';
import MultiControlSize from './ProductControls/multi/MultiControlSize';
import MultiControlTechnology from './ProductControls/multi/MultiControlTechnology';
import {
  MultiProductControlProps,
  ControlOptionProps,
  SingleProductControlProps,
} from './ProductControls/ProductControlProps';
import DropDownControl from './ProductControls/single/DropdownControl';
import SingleControl from './ProductControls/single/SingleControl';
import SingleControlTechnology from './ProductControls/single/SingleControlTechnology';

export function getMultiControl(controlUiType: ControlUIType): React.FC<MultiProductControlProps> {
  switch (controlUiType) {
    case ControlUIType.Size:
      return MultiControlSize;
    case ControlUIType.Technology:
      return MultiControlTechnology;
    default:
      return MultiControl;
  }
}

export function getSingleControl(controlUiType: ControlUIType): React.FC<SingleProductControlProps> {
  switch (controlUiType) {
    case ControlUIType.Dropdown:
      return DropDownControl;
    case ControlUIType.Technology:
      return SingleControlTechnology;
    default:
      return SingleControl;
  }
}

export function getControlElement(controlUiType: ControlUIType, multiMode: boolean): React.FC<ControlOptionProps> {
  switch (controlUiType) {
    case ControlUIType.Button:
      return RadioElement;
    case ControlUIType.Color:
      return ColorElement as React.FC<ControlOptionProps>;
    case ControlUIType.Material:
      return MaterialElement as React.FC<ControlOptionProps>;
    case ControlUIType.Foil:
      return ColorElement as React.FC<ControlOptionProps>;
    case ControlUIType.Switcher:
      return ButtonElement as React.FC<ControlOptionProps>;
    default:
      return multiMode ? CheckboxElement : RadioElement;
  }
}

// Cache for expensive operations
const availableOptionsCache = new Map<string, { [key: string]: Set<string> }>();
const unavailableOptionsCache = new Map<string, { [key: string]: { [option: string]: ProductUnAvailability[] } }>();

// Helper function to create cache key
function createCacheKey(
  multiOptions: MultiOptions,
  singleOptions: SingleOptions,
  controlsUIOrder: string[],
  unavailableProductsKeys: string[]
): string {
  const multiOptionsStr = Object.entries(multiOptions)
    .map(([key, options]) => `${key}:${options.map(o => o.value).sort().join(',')}`)
    .sort()
    .join('|');
  const singleOptionsStr = Object.entries(singleOptions)
    .sort()
    .map(([key, value]) => `${key}:${value}`)
    .join('|');
  const controlsStr = controlsUIOrder.join(',');
  const unavailableStr = unavailableProductsKeys.sort().join(',');
  
  return `${multiOptionsStr}§${singleOptionsStr}§${controlsStr}§${unavailableStr}`;
}

// Optimized variation filtering using Sets for O(1) lookups
function filterVariationsBasedOnControlKey(
  multiOptions: MultiOptions,
  singleOptions: SingleOptions,
  filteredVariations: ProductVariation[],
  controlKey: string,
) {
  // Pre-build lookup sets for faster filtering
  const multiOptionSets = new Map<string, Set<string>>();
  for (const key in multiOptions) {
    if (key !== controlKey) {
      multiOptionSets.set(key, new Set(multiOptions[key].map(option => option.value)));
    }
  }

  return filteredVariations.filter((variation) => {
    // Check single options first (usually fewer)
    for (const key in singleOptions) {
      if (key !== controlKey && variation[key] !== singleOptions[key]) {
        return false;
      }
    }

    // Check multi options using pre-built sets
    for (const [key, valueSet] of multiOptionSets) {
      if (!valueSet.has(variation[key])) {
        return false;
      }
    }

    return true;
  });
}

export function getAvailableOptions(
  multiOptions: MultiOptions,
  singleOptions: SingleOptions,
  variations: ProductVariation[],
  controlsUIOrder: string[],
  unavailableProducts: Product['unavailableProducts'],
) {
  // Create enhanced cache key that includes variation content hash to auto-invalidate on data changes
  const unavailableProductsKeys = Object.keys(unavailableProducts);
  const variationsHash = generateVariationsHash(variations);
  const cacheKey = createEnhancedCacheKey(
    multiOptions, 
    singleOptions, 
    controlsUIOrder, 
    unavailableProductsKeys,
    variations.length,
    variationsHash
  );
  
  // Return cached result if available (and refresh its position in LRU)
  const cached = availableOptionsCache.get(cacheKey);
  if (cached) {
    // Refresh LRU position by deleting and re-inserting
    availableOptionsCache.delete(cacheKey);
    availableOptionsCache.set(cacheKey, cached);
    return cached;
  }

  // Pre-filter unavailable products once
  const availableVariations = unavailableProductsKeys.length > 0
    ? variations.filter((variation) => !unavailableProducts[variation.productUid])
    : variations;

  if (!availableVariations.length) {
    const emptyResult = controlsUIOrder.reduce<{ [key: string]: Set<string> }>((acc, controlKey) => {
      acc[controlKey] = new Set();
      return acc;
    }, {});
    availableOptionsCache.set(cacheKey, emptyResult);
    return emptyResult;
  }

  const result = controlsUIOrder.reduce<{ [key: string]: Set<string> }>((acc, controlKey) => {
    const filteredForControl = filterVariationsBasedOnControlKey(
      multiOptions,
      singleOptions,
      availableVariations,
      controlKey,
    );

    acc[controlKey] = filteredForControl.length > 0 
      ? new Set(filteredForControl.map((variation) => variation[controlKey]))
      : new Set();

    return acc;
  }, {});

  // Cache the result
  availableOptionsCache.set(cacheKey, result);
  
  // Limit cache size to prevent memory leaks
  if (availableOptionsCache.size > 100) {
    const firstKey = availableOptionsCache.keys().next().value;
    if (firstKey) {
      availableOptionsCache.delete(firstKey);
    }
  }

  return result;
}

export function getUnAvailableOptions(
  multiOptions: MultiOptions,
  singleOptions: SingleOptions,
  variations: ProductVariation[],
  controlsUIOrder: string[],
  unavailableProducts: Product['unavailableProducts'],
) {
  // Create enhanced cache key that includes variation content hash to auto-invalidate on data changes
  const unavailableProductsKeys = Object.keys(unavailableProducts);
  const variationsHash = generateVariationsHash(variations);
  const cacheKey = createEnhancedCacheKey(
    multiOptions, 
    singleOptions, 
    controlsUIOrder, 
    unavailableProductsKeys,
    variations.length,
    variationsHash
  );
  
  // Return cached result if available (and refresh its position in LRU)
  const cached = unavailableOptionsCache.get(cacheKey);
  if (cached) {
    // Refresh LRU position by deleting and re-inserting
    unavailableOptionsCache.delete(cacheKey);
    unavailableOptionsCache.set(cacheKey, cached);
    return cached;
  }

  // Pre-filter unavailable products once
  const unavailableVariations = unavailableProductsKeys.length > 0
    ? variations.filter((variation) => !!unavailableProducts[variation.productUid])
    : [];

  if (!unavailableVariations.length) {
    const emptyResult = controlsUIOrder.reduce<{ [key: string]: { [option: string]: ProductUnAvailability[] } }>((acc, controlKey) => {
      acc[controlKey] = {};
      return acc;
    }, {});
    unavailableOptionsCache.set(cacheKey, emptyResult);
    return emptyResult;
  }

  const result = controlsUIOrder.reduce<{ [key: string]: { [option: string]: ProductUnAvailability[] } }>((acc, controlKey) => {
    const filteredForControl = filterVariationsBasedOnControlKey(
      multiOptions,
      singleOptions,
      unavailableVariations,
      controlKey,
    );

    if (filteredForControl.length) {
      acc[controlKey] = filteredForControl.reduce(
        (accum, variation) => {
          const unAvailabilityReason = unavailableProducts[variation.productUid];
          accum[variation[controlKey]] = formatUnavailabilityReason(unAvailabilityReason);
          return accum;
        },
        {} as { [option: string]: ProductUnAvailability[] },
      );
    } else {
      acc[controlKey] = {};
    }

    return acc;
  }, {});

  // Cache the result
  unavailableOptionsCache.set(cacheKey, result);
  
  // Limit cache size to prevent memory leaks
  if (unavailableOptionsCache.size > 100) {
    const firstKey = unavailableOptionsCache.keys().next().value;
    if (firstKey) {
      unavailableOptionsCache.delete(firstKey);
    }
  }

  return result;
}

const formatUnavailabilityReason = (reason: ProductUnAvailability[] | true): ProductUnAvailability[] => {
  if (reason === true) {
    return ['region_availability'];
  }
  return reason;
};

// Utility function to clear caches when product data changes
export function clearVariantCaches() {
  availableOptionsCache.clear();
  unavailableOptionsCache.clear();
}

// Enhanced cache key with product data fingerprint to automatically detect stale data
function createEnhancedCacheKey(
  multiOptions: MultiOptions,
  singleOptions: SingleOptions,
  controlsUIOrder: string[],
  unavailableProductsKeys: string[],
  variationsCount: number,
  variationsHash: string
): string {
  const baseKey = createCacheKey(multiOptions, singleOptions, controlsUIOrder, unavailableProductsKeys);
  // Include variation count AND content hash to invalidate when variations actually change
  return `${baseKey}:${variationsCount}:${variationsHash}`;
}

// Generate a simple hash from variations content to detect actual changes
function generateVariationsHash(variations: ProductVariation[]): string {
  if (variations.length === 0) return 'empty';
  if (variations.length > 100) {
    // For large datasets, use first/last few items to avoid performance hit
    const sample = [...variations.slice(0, 5), ...variations.slice(-5)];
    return sample.map(v => `${v.productUid}-${Object.keys(v).length}`).join('|');
  }
  // For smaller datasets, create more comprehensive hash
  return variations.map(v => `${v.productUid}-${Object.keys(v).length}`).join('|');
}

const regionUnAvailabilityMessage = 'This product variant is not available in your region';
const dispatchSlaUnavailabilityMessage = 'This product is not available for TikTok';

export function getUnAvailableMessage(option: string, unavailableOptions?: { [key: string]: ProductUnAvailability[] }) {
  return unavailableOptions?.[option]?.map((unAvailabilityReason) =>
    unAvailabilityReason === 'region_availability' ? regionUnAvailabilityMessage : dispatchSlaUnavailabilityMessage,
  );
}
